import type { LoadContext } from '@microsoft/teams-js';
import { getBootstrapError } from '@ms/yammer-data/dist/domains/session/selectors';
import { startCachedAppPageLoadTiming } from '@ms/yammer-page-route-telemetry';
import { endAndReportPerformanceMeasure, reportUsage, startPerformanceMeasure } from '@ms/yammer-telemetry';
import { loadIsExpectedError } from '@ms/yammer-web-support/dist/errors';
import { notifyAppExpectedFailure, notifyAppInitializationFailure } from '@ms/yammer-web-support/dist/metaOsHub';
import { loadContext, tryHandleDeepLink } from 'domains/teams/actions';
import { getAppLaunchInitiator } from 'state/teamsContext/selectors';
import type { YammerTeamsNativeState } from 'state/types';
import { updateClientContextWithHostMetadata } from 'teams/clientContext';

import { cancelPendingCacheSync } from './serviceWorkerCacheSync';
import { subscribeTeamsToNextPageLifecycleEvent } from './teamsPageLifecycleEvents';

type HandleOnLoadAppFromCache = (appLoadContext: LoadContext, getState: () => YammerTeamsNativeState) => Promise<void>;

export const handleOnLoadAppFromCache: HandleOnLoadAppFromCache = async (appLoadContext, getState) => {
  cancelPendingCacheSync();
  const measure = startPerformanceMeasure('cached_bootstrap');
  let endedWithError = false;
  let endedWithExpectedError = false;

  try {
    const bootstrapError = getBootstrapError(getState());

    if (bootstrapError) {
      await notifyAppExpectedFailure();
      endedWithError = true;
      endAndReportPerformanceMeasure({
        ...measure,
        eventProperties: { ...measure.eventProperties, endedWithError, isExpectedError: true },
      });
      window.location.reload();

      return;
    }

    subscribeTeamsToNextPageLifecycleEvent();
    startCachedAppPageLoadTiming();

    await loadContext();

    updateClientContextWithHostMetadata();

    tryHandleDeepLink(true, appLoadContext.contentUrl.toString());

    const initiator = getAppLaunchInitiator();

    reportUsage({
      eventName: 'teams_app_loaded_from_cache',
      eventProperties: {
        initiator,
      },
    });
  } catch (error: unknown) {
    endedWithError = true;

    const { isExpectedError } = await loadIsExpectedError();
    if (isExpectedError(error)) {
      endedWithExpectedError = true;
      notifyAppExpectedFailure(error);
    } else {
      notifyAppInitializationFailure(error);
    }
  } finally {
    endAndReportPerformanceMeasure({
      ...measure,
      eventProperties: {
        ...measure.eventProperties,
        endedWithError,
        isExpectedError: endedWithExpectedError,
      },
    });
  }
};
